import type { License, OrganizationSummary, Role } from "../api/contract";
import type { MenuItem } from "../components/types";

// Presentation helpers for the org/plan shell chrome (topbar plan badge + org
// switcher). Kept as pure functions in a plain .ts module so both are unit-testable
// without mounting AppShell.

/**
 * Text for the topbar plan badge. The contract carries `plan` (the customer-facing
 * subscription name) and `accessLevel` (the entitlement tier) independently, and for
 * most tiers the backend returns the SAME value for both (e.g. plan "pro" /
 * accessLevel "pro") — which rendered as a duplicated "pro · pro". Collapse
 * semantically identical values (case- and whitespace-insensitive) to a single token;
 * render genuinely different values as `plan · accessLevel` — plan first, matching the
 * Overview "Plan" card (value = plan, sub = "{accessLevel} access").
 */
export function planBadgeLabel(license: Pick<License, "plan" | "accessLevel">): string {
  const plan = license.plan.trim();
  const access = license.accessLevel.trim();
  if (!plan) return tierLabel(access);
  if (!access) return tierLabel(plan);
  if (isBuilderTier(plan) && isBuilderTier(access)) return "Builders";
  if (plan.toLowerCase() === access.toLowerCase()) return plan;
  return `${tierLabel(plan)} · ${tierLabel(access)}`;
}

function isBuilderTier(value: string): boolean {
  const normalized = value.toLowerCase();
  return normalized === "builder" || normalized === "builders";
}

function tierLabel(value: string): string {
  return isBuilderTier(value) ? "Builders" : value;
}

const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member"
};

function roleLabel(role: Role): string {
  return ROLE_LABELS[role] ?? role;
}

/**
 * Build the organization-switcher menu items. A single verified email can own several
 * license groups whose plan/access labels collide (all "pro"), so the plan tier alone
 * can't tell two orgs apart. Each option is disambiguated with identity that IS on the
 * session summary — the org's own `slug` — plus the viewer's role; the org you're
 * currently in is marked and made non-selectable so "you are here" is unambiguous.
 * (Per-org license/seat context is not on the session summary and is not fetched here —
 * we don't invent backend data to fill the menu.)
 */
export function orgSwitcherItems(
  organizations: OrganizationSummary[],
  currentOrgId: string | null,
  navigate: (orgId: string) => void
): MenuItem[] {
  return organizations.map((org) => {
    const current = org.id === currentOrgId;
    const context = [org.slug, roleLabel(org.role)];
    if (current) context.push("current");
    return {
      id: org.id,
      label: org.name,
      description: context.join(" · "),
      disabled: current,
      onSelect: () => navigate(org.id)
    };
  });
}

/** Prefer the last customer context without treating organization ownership as access. */
export function preferredPortalOrganization(
  organizations: OrganizationSummary[],
  preferredOrgId: string | null
): OrganizationSummary | null {
  return organizations.find((organization) => organization.id === preferredOrgId) ?? organizations[0] ?? null;
}
